/**
 * Band severity and colour, shared by the recordings bar, the timeline and the
 * rule editor's live preview.
 *
 * Band 0 is "ok" and is never stored as a violation, so the ramp below starts at
 * the benign end and every stored band indexes into position 1 or higher. A rule
 * may declare more bands than the ramp has entries; the last colour repeats
 * rather than wrapping, because wrapping would paint the most severe band with
 * the calmest colour.
 */

/** Ordered severity ramp. Index is the band number. */
export const BAND_RAMP = [
  { key: 'ok', label: 'OK', color: '#E4E8EE', textColor: '#5A6472' },
  { key: 'low', label: 'Low', color: '#F2C94C', textColor: '#4A3B00' },
  { key: 'medium', label: 'Medium', color: '#F2994A', textColor: '#4A2600' },
  { key: 'high', label: 'High', color: '#EB5757', textColor: '#FFFFFF' },
  { key: 'critical', label: 'Critical', color: '#8E24AA', textColor: '#FFFFFF' },
];

export const bandStyle = (band) => {
  if (!Number.isFinite(band) || band <= 0) return BAND_RAMP[0];
  return BAND_RAMP[Math.min(band, BAND_RAMP.length - 1)];
};

export const bandColor = (band) => bandStyle(band).color;

/**
 * How a recording's summary should be presented on the list.
 *
 * The states are deliberately distinct rather than collapsing into
 * broken/not-broken: a recording that has not been scored yet, and one whose
 * evaluation FAILED, must never render the same as one that came back clean.
 * Showing "clean" for either would be a false all-clear.
 */
export const summaryState = (summary) => {
  if (!summary || !summary.status) return 'unscored';
  if (summary.status === 'PENDING') return 'pending';
  if (summary.status === 'FAILED') return 'failed';
  return summary.brokenRuleCount > 0 ? 'broken' : 'clean';
};

/** True when the summary was computed against a rule set that has since changed. */
export const isSummaryStale = (summary, rules) => {
  if (!summary || summary.status !== 'COMPLETE') return false;
  const evaluated = summary.evaluatedRuleVersions || {};
  const applicable = (rules || []).filter((r) => evaluated[r.id] !== undefined);
  if (applicable.some((r) => evaluated[r.id] !== r.version)) return true;
  return Object.keys(evaluated).length !== applicable.length;
};

export const bucketsOf = (summary) => {
  const buckets = summary?.densityBuckets;
  if (!Array.isArray(buckets) || buckets.length === 0) return [];
  return buckets;
};

/** "3 rules" / "1 rule" / "Clean" — the text beside the bar and in the tooltip. */
export const summaryLabel = (summary) => {
  switch (summaryState(summary)) {
    case 'pending':
      return 'Scoring…';
    case 'failed':
      return 'Scoring failed';
    case 'unscored':
      return 'Not scored';
    case 'clean':
      return 'Clean';
    default: {
      const n = summary.brokenRuleCount;
      return `${n} rule${n === 1 ? '' : 's'}`;
    }
  }
};
